(() => {
  'use strict';

  const STEPS = [[0, 'Uploading your photo…'], [12, 'Studying the details…'], [40, 'Sculpting your design…'],
    [70, 'Polishing the metal…'], [88, 'Almost ready…']];
  const POLL_MS = 1200;
  const POLL_LIMIT_MS = 5 * 60 * 1000;
  const TONE_LABEL = { silver: 'silver', gold: 'gold', rose: 'rose gold' };

  const toneOf = (name) => {
    const n = String(name || '').toLowerCase();
    if (n.includes('rose')) return 'rose';
    if (n.includes('white')) return 'silver';
    if (n.includes('gold')) return 'gold';
    return 'silver';
  };

  function track(event, data = {}) {
    try { (window.dataLayer = window.dataLayer || []).push({ event: `photo_earring_${event}`, ...data }); } catch (e) {}
    try {
      if (typeof window.fbq === 'function') {
        if (event === 'preview_ready') window.fbq('track', 'CustomizeProduct');
        else window.fbq('trackCustom', `PhotoEarring_${event}`, data);
      }
    } catch (e) {}
  }

  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!m) return (cents / 100).toFixed(2);
    const n = cents / 100;
    const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    let v;
    switch (m[1]) {
      case 'amount_no_decimals': v = group(Math.round(n).toString(), ','); break;
      case 'amount_with_comma_separator': v = group(n.toFixed(2).replace('.', ','), '.'); break;
      case 'amount_no_decimals_with_comma_separator': v = group(Math.round(n).toString(), '.'); break;
      default: { const [a, b] = n.toFixed(2).split('.'); v = `${group(a, ',')}.${b}`; }
    }
    return fmt.replace(m[0], v);
  }

  function loadFile(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => resolve({ img, url });
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
      img.src = url;
    });
  }
  function toJpeg(img, maxSide = 2048) {
    const s = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.round(img.naturalWidth * s); c.height = Math.round(img.naturalHeight * s);
    const x = c.getContext('2d');
    x.fillStyle = '#fff'; x.fillRect(0, 0, c.width, c.height);
    x.drawImage(img, 0, 0, c.width, c.height);
    return new Promise((resolve) => c.toBlob((b) => resolve(b), 'image/jpeg', 0.9));
  }
  const cleanText = (s) => String(s || '').replace(/[^\p{L}\p{N} &'.\-]/gu, '');

  class PhotoEarrings {
    constructor(root) {
      this.root = root;
      this.cfg = JSON.parse(root.querySelector('[data-pe-config]').textContent);
      this.q = (s) => root.querySelector(s);
      this.qa = (s) => Array.from(root.querySelectorAll(s));
      this.storeKey = `pe:${this.cfg.productId}`;
      this.photoBlob = null;
      this.photoUrl = null;
      this.busy = false;
      this.state = { designs: [], active: null, used: 0, text: '', photo: null };
      this.restore();
      const v = this.cfg.variants.find((x) => x.id === this.cfg.currentVariant) || this.cfg.variants[0];
      this.options = v ? [...v.options] : [];
      this.bind();
      const t = this.q('[data-text]');
      if (t) t.value = this.state.text || '';
      this.renderAll();
    }

    restore() {
      try {
        const s = JSON.parse(sessionStorage.getItem(this.storeKey) || 'null');
        if (s && Array.isArray(s.designs)) this.state = { ...this.state, ...s, designs: s.designs.filter((d) => !d.demo) };
        if (!this.state.designs.find((d) => d.id === this.state.active)) this.state.active = this.state.designs.at(-1)?.id || null;
      } catch (e) {}
    }
    persist() {
      try { sessionStorage.setItem(this.storeKey, JSON.stringify({ ...this.state, designs: this.state.designs.filter((d) => !d.demo) })); } catch (e) {}
    }

    get variant() { return this.cfg.variants.find((v) => v.options.every((o, i) => o === this.options[i])) || null; }
    get metal() { return this.cfg.metalIndex >= 0 ? this.options[this.cfg.metalIndex] : ''; }
    get tone() { return toneOf(this.metal); }
    get text() { return cleanText(this.state.text).trim(); }
    get active() { return this.state.designs.find((d) => d.id === this.state.active) || null; }

    bind() {
      this.qa('input[data-option]').forEach((input) => input.addEventListener('change', () => {
        this.options[Number(input.dataset.option)] = input.value;
        this.renderAll();
        const v = this.variant;
        if (v) { const u = new URL(window.location.href); u.searchParams.set('variant', v.id); window.history.replaceState({}, '', u.toString()); }
      }));
      const text = this.q('[data-text]');
      text.addEventListener('input', () => {
        const clean = cleanText(text.value).slice(0, this.cfg.maxText);
        if (clean !== text.value) text.value = clean;
        this.state.text = clean; this.persist(); this.renderCounter(); this.renderLook(); this.renderButton();
      });
      const file = this.q('[data-file]');
      file.multiple = true; // iPhone picker: multi-pick mode always shows the tick / Add button (the first photo is used)
      file.addEventListener('change', () => { if (file.files[0]) this.handleFile(file.files[0]); file.value = ''; });
      const drop = this.q('[data-dropzone]');
      ['dragenter', 'dragover'].forEach((e) => drop.addEventListener(e, (ev) => { ev.preventDefault(); drop.classList.add('is-drag'); }));
      ['dragleave', 'drop'].forEach((e) => drop.addEventListener(e, (ev) => { ev.preventDefault(); drop.classList.remove('is-drag'); }));
      drop.addEventListener('drop', (ev) => { const f = ev.dataTransfer?.files?.[0]; if (f) this.handleFile(f); });
      this.q('[data-regen]').addEventListener('click', () => this.generate());
      this.q('[data-update]').addEventListener('click', () => this.generate());
      this.qa('[data-qty]').forEach((b) => b.addEventListener('click', () => {
        const input = this.q('[data-qty-input]');
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.qty));
      }));
      this.q('[data-atc]').addEventListener('click', () => this.addToCart());
      this.qa('[data-gallery-thumb]').forEach((t) => t.addEventListener('click', () => this.showGallery(t.dataset.src, t.dataset.mediaId)));
    }

    // ------------------------------------------------ render
    renderAll() { this.renderPrice(); this.renderCounter(); this.renderLook(); this.renderVersions(); this.renderButton(); }
    renderPrice() {
      const v = this.variant;
      const name = this.q('[data-metal-name]');
      if (name) name.textContent = this.metal;
      if (!v) return;
      this.q('[data-price-now]').textContent = formatMoney(v.price, this.cfg.moneyFormat);
      const was = this.q('[data-price-was]');
      if (v.compare > v.price) { was.hidden = false; was.textContent = formatMoney(v.compare, this.cfg.moneyFormat); } else was.hidden = true;
    }
    renderCounter() { const el = this.q('[data-text-count]'); if (el) el.textContent = `${(this.state.text || '').length}/${this.cfg.maxText}`; }
    showGallery(src, mediaId) {
      const main = this.q('[data-gallery-main]');
      if (!main || !src) return;
      if (main.getAttribute('src') !== src) { main.removeAttribute('srcset'); main.src = src; }
      this.qa('[data-gallery-thumb]').forEach((t) => t.classList.toggle('is-active', String(t.dataset.mediaId) === String(mediaId)));
    }
    renderLook() {
      const d = this.active;
      const look = this.q('[data-look]');
      look.hidden = !d;
      this.q('[data-done]').hidden = !d || this.busy;
      if (!d) return;
      const before = this.q('[data-before]');
      const src = this.photoUrl || this.state.photo;
      before.parentElement.hidden = !src;
      if (src && before.getAttribute('src') !== src) before.src = src;
      const after = this.q('[data-after]');
      if (after.getAttribute('src') !== d.url) after.src = d.url;
      this.q('[data-demo]').hidden = !d.demo;

      // the design no longer matches the choices: offer a refresh
      const update = this.q('[data-update]');
      const left = Math.max(0, this.cfg.maxGenerations - this.state.used);
      let label = '';
      if (!d.demo && (d.text || '') !== this.text) label = this.text ? `Update preview with “${this.text}”` : 'Update preview without a name';
      else if (!d.demo && d.tone !== this.tone) label = `See it in ${TONE_LABEL[this.tone]}`;
      update.hidden = !label || left <= 0 || !this.photoBlob;
      update.textContent = label;
      const regen = this.q('[data-regen]');
      regen.hidden = left <= 0 || !this.photoBlob;
      this.q('[data-regen-left]').textContent = left > 0 ? `(${left} left)` : '';
      let msg = this.root.dataset.doneMsg || (this.root.dataset.doneMsg = this.q('[data-done-msg]').textContent);
      if (!d.demo && d.tone !== this.tone && !label.startsWith('Update')) msg = `Shown in ${TONE_LABEL[d.tone]}. Your pair will be made in ${this.metal}.`;
      if (d.demo) msg = 'This is a sample. Your own preview appears here once our design studio is connected.';
      this.q('[data-done-msg]').textContent = msg;
    }
    renderVersions() {
      const wrap = this.q('[data-versions]');
      const list = this.q('[data-versions-list]');
      const ds = this.state.designs;
      wrap.hidden = ds.length < 2;
      list.innerHTML = '';
      ds.forEach((d, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ct-version' + (d.id === this.state.active ? ' is-active' : '');
        b.setAttribute('aria-label', `Design ${i + 1}`);
        const im = document.createElement('img'); im.src = d.url; im.alt = ''; im.loading = 'lazy';
        b.appendChild(im);
        b.addEventListener('click', () => { this.state.active = d.id; this.persist(); this.renderLook(); this.renderVersions(); this.renderButton(); });
        list.appendChild(b);
      });
    }
    renderButton() {
      const btn = this.q('[data-atc]');
      const label = this.q('[data-atc-label]');
      const v = this.variant;
      const d = this.active;
      if (!v) { btn.disabled = true; label.textContent = 'Unavailable'; return; }
      if (!v.available) { btn.disabled = true; label.textContent = 'Sold out'; return; }
      if (this.busy) { btn.disabled = true; label.textContent = 'Making your design…'; return; }
      if (!d) { btn.disabled = true; label.textContent = 'Upload a photo to continue'; return; }
      if (!d.demo && this.photoBlob && (d.text || '') !== this.text) { btn.disabled = true; label.textContent = 'Update your preview to continue'; return; }
      btn.disabled = false;
      label.textContent = `Add to cart · ${formatMoney(v.price, this.cfg.moneyFormat)}`;
    }

    // ------------------------------------------------ upload + generate
    setError(msg) { const el = this.q('[data-error]'); el.textContent = msg || ''; el.hidden = !msg; }

    async handleFile(file) {
      this.setError('');
      const warn = this.q('[data-warn]');
      warn.hidden = true;
      if (file.size > 80 * 1024 * 1024) return this.setError('That file is too large. Please choose a photo.');
      let loaded;
      try { loaded = await loadFile(file); } catch (e) { return this.setError("We couldn't open that file. Please choose a JPG or PNG photo."); }
      const { img, url } = loaded;
      if (Math.min(img.naturalWidth, img.naturalHeight) < this.cfg.minPhotoSide) {
        warn.textContent = 'This photo is small, so fine details may be lost. Try a larger one if you have it.';
        warn.hidden = false;
      }
      const thumb = this.q('[data-thumb]');
      thumb.innerHTML = '';
      const t = document.createElement('img'); t.src = url; t.alt = ''; thumb.appendChild(t);
      this.q('[data-upload-title]').textContent = 'Your photo';
      this.q('[data-upload-sub]').textContent = file.name;
      this.q('[data-upload-action]').textContent = 'Replace';
      this.photoUrl = url;
      this.photoBlob = await toJpeg(img);
      // keep a small copy of the photo so the before/after survives a page reload
      try {
        const s = Math.min(1, 360 / Math.max(img.naturalWidth, img.naturalHeight));
        const c = document.createElement('canvas'); c.width = img.naturalWidth * s; c.height = img.naturalHeight * s;
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        this.state.photo = c.toDataURL('image/jpeg', 0.8);
      } catch (e) {}
      this.state.used = 0;
      track('photo_uploaded', { width: img.naturalWidth, height: img.naturalHeight });
      this.generate();
    }

    setBusy(on) {
      this.busy = on;
      this.q('[data-status]').hidden = !on;
      this.root.classList.toggle('is-busy', on);
      this.renderLook();
      this.renderButton();
    }
    startProgress(expected) {
      this.stopProgress();
      this.progress = 0; this.progressFloor = 0;
      const exp = expected || this.cfg.expectedSeconds || 20;
      const tau = Math.max(1, exp / 2.3);
      const t0 = Date.now();
      const tick = () => {
        const t = (Date.now() - t0) / 1000;
        let p = 95 * (1 - Math.exp(-t / tau));
        if (t > exp) p = Math.min(98, p + (t - exp) * 0.15);
        this.setProgress(Math.max(p, this.progressFloor));
      };
      tick();
      this.progressTimer = setInterval(tick, 200);
    }
    stopProgress() { if (this.progressTimer) clearInterval(this.progressTimer); this.progressTimer = null; }
    setProgress(p) {
      this.progress = Math.max(this.progress || 0, p);
      const pct = Math.round(this.progress);
      this.q('[data-bar-fill]').style.width = `${this.progress}%`;
      this.q('[data-bar]').setAttribute('aria-valuenow', pct);
      this.q('[data-pct]').textContent = `${pct}%`;
      const step = STEPS.filter(([at]) => this.progress >= at).pop();
      if (step) this.q('[data-status-msg]').textContent = step[1];
    }
    async finishProgress() {
      this.stopProgress(); this.setProgress(100);
      this.q('[data-status-msg]').textContent = 'Done!';
      await new Promise((r) => setTimeout(r, 400));
    }
    addDesign(d) {
      this.state.designs.push(d);
      this.state.active = d.id;
      this.persist();
    }

    async generate() {
      if (this.busy) return;
      if (!this.photoBlob) { this.q('[data-file]').click(); return; }
      if (this.state.used >= this.cfg.maxGenerations) {
        return this.setError("You've used all your tries for this photo. Pick your favorite design, or upload a different photo.");
      }
      this.setError('');
      const started = Date.now();
      const tone = this.tone, text = this.text;

      // No server yet: show a sample render so the page can be reviewed end to end.
      if (!this.cfg.api) {
        this.setBusy(true);
        this.startProgress(2.5);
        await new Promise((r) => setTimeout(r, 2200));
        await this.finishProgress();
        const url = this.cfg.samples[tone] || this.cfg.samples.silver || this.cfg.samples.gold || this.cfg.samples.rose;
        this.state.designs = this.state.designs.filter((d) => !d.demo);
        this.addDesign({ id: `demo-${Date.now()}`, demo: true, url, tone, text });
        this.setBusy(false);
        this.renderAll();
        return;
      }

      this.setBusy(true);
      this.startProgress();
      track('generate_start', { attempt: this.state.used + 1, tone, has_text: !!text });
      try {
        const fd = new FormData();
        fd.append('photo', this.photoBlob, 'photo.jpg');
        fd.append('product', this.cfg.productKind || 'relief-earring');
        fd.append('metal', tone);
        if (text) fd.append('text', text);
        const res = await fetch(`${this.cfg.api}/api/generate`, { method: 'POST', body: fd });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.job) throw new Error(data.error || 'Something went wrong. Please try again.');
        this.state.used += 1;
        this.persist();
        this.progressFloor = 12;
        while (true) {
          await new Promise((r) => setTimeout(r, POLL_MS));
          if (Date.now() - started > POLL_LIMIT_MS) throw new Error('This is taking longer than usual. Please try again.');
          const job = await (await fetch(`${this.cfg.api}/api/jobs/${data.job}`)).json().catch(() => ({}));
          if (job.status === 'done') {
            const url = job.design.previews.render.url;
            await new Promise((r) => { const i = new Image(); i.onload = i.onerror = r; i.src = url; });
            await this.finishProgress();
            this.addDesign({ id: job.design.id, url, tone, text });
            track('preview_ready', { seconds: Math.round((Date.now() - started) / 1000), attempt: this.state.used });
            break;
          }
          if (job.status === 'error') throw new Error(job.error || 'Something went wrong. Please try again.');
          if (job.stage === 'finishing') this.progressFloor = 88;
        }
        this.setBusy(false);
        this.renderAll();
      } catch (e) {
        this.stopProgress();
        this.setBusy(false);
        this.setError(e.message);
        track('generate_error', { message: e.message });
      }
    }

    // ------------------------------------------------ cart
    async addToCart() {
      const v = this.variant;
      const d = this.active;
      if (!v || !d) return;
      const btn = this.q('[data-atc]');
      btn.disabled = true; btn.classList.add('is-loading');
      const qty = Math.max(1, parseInt(this.q('[data-qty-input]').value, 10) || 1);
      const properties = { Photo: 'Sculpted from your photo' };
      if (d.text) properties.Name = d.text;
      properties._kind = this.cfg.productKind || 'relief-earring';
      if (!d.demo) { properties._design_id = d.id; properties._preview = d.url; properties._preview_tone = d.tone; }
      const drawer = document.querySelector('cart-drawer');
      const sections = typeof window.upcartOpenCart !== 'function' && drawer && typeof drawer.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map((s) => s.id) : [];
      try {
        const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: v.id, quantity: qty, properties }], sections: sections.length ? sections : undefined, sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart.');
        track('add_to_cart', { variant: v.id, metal: this.metal, has_text: !!d.text });
        // the store's cart drawer app (UpCart) hides the theme drawer: refresh and open it like every other product
        if (typeof window.upcartOpenCart === 'function') {
          try { if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart(); } catch (x) {}
          setTimeout(() => { try { window.upcartOpenCart(); } catch (x) {} }, 300);
        } else if (drawer && this.cfg.cartType === 'drawer' && typeof drawer.renderContents === 'function' && data.sections) {
          const item = (data.items && data.items[0]) || data;
          item.sections = data.sections;
          drawer.classList.remove('is-empty');
          drawer.renderContents(item);
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
      } catch (e) {
        this.setError(e.message);
      } finally {
        btn.classList.remove('is-loading');
        this.renderButton();
      }
    }
  }

  const init = () => document.querySelectorAll('.pe[data-section]').forEach((el) => { if (!el.__pe) el.__pe = new PhotoEarrings(el); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
